import { useT } from "@/i18n";
import { resolveLocalized } from "@pervasivemind/dashboard-render";
import { ELEMENT_LABEL, findItem, findSection } from "@/lib/dashboard-helpers";
import { DashboardInspector } from "@/pages/builder/inspector/DashboardInspector";
import { ItemInspector } from "@/pages/builder/inspector/ItemInspector";
import { SectionInspector } from "@/pages/builder/inspector/SectionInspector";
import type { UseBuilderDraft } from "@/hooks/useBuilderDraft";

interface SectionEditorProps {
  draft: UseBuilderDraft;
}

/**
 * Right-side inspector pane of the Dashboard tab.
 *
 * Iter 21 refactor — was a full editor with chart-type pickers,
 * KPI variant selectors, format dropdowns, filter editors, etc.
 * Replaced by READ-ONLY inspector cards that keep inline editors
 * only for the fields the admin still curates from the UI:
 * bilingual labels (display_name, description, KPI label, chart
 * title, column header, filter label) + synonyms.
 *
 * Structural edits flow through Claude via the report-builder skill;
 * the canvas pane on the left carries the "Read-only" banner.
 *
 * Three display modes based on current selection:
 *   1. Item selected   → ItemInspector (KPI / chart / table / text)
 *   2. Section selected → SectionInspector
 *   3. Nothing selected → DashboardInspector (report identity +
 *      global filters)
 */
export function SectionEditor({ draft }: SectionEditorProps): JSX.Element {
  const { t, locale } = useT();
  const { selection, dashboard } = draft;
  const section = findSection(dashboard, selection.sectionId);
  const item = findItem(dashboard, selection.sectionId, selection.itemId);

  return (
    <aside className="flex h-full flex-col overflow-y-auto border-l border-neutral-200 bg-white">
      <div className="border-b border-neutral-200 px-4 py-3">
        <h2 className="text-sm font-semibold text-neutral-900">
          {item
            ? t("inspector.itemTitle", {
                element: ELEMENT_LABEL[item.element],
              })
            : section
              ? resolveLocalized(section.title, locale) ||
                t("inspector.sectionFallback")
              : t("inspector.dashboardTitle")}
        </h2>
        <p className="mt-0.5 text-xs text-neutral-500">
          {item
            ? t("inspector.itemHint")
            : section
              ? t("inspector.sectionHint")
              : t("inspector.dashboardHint")}
        </p>
      </div>

      <div className="flex-1 px-4 py-4">
        {item && section ? (
          <ItemInspector
            draft={draft}
            sectionId={section.id}
            itemId={item.id}
          />
        ) : section ? (
          <SectionInspector draft={draft} sectionId={section.id} />
        ) : (
          <DashboardInspector draft={draft} />
        )}
      </div>
    </aside>
  );
}
